import {
  APIBroadcastAttributes,
  APIBroadcastInstanceProgressResponse,
  ArdoqId,
  BroadcastStatus,
  BroadcastAudience,
  BroadcastContent,
  ComponentPreviewResponse,
  BroadcastInstance,
  BroadcastPreview,
  Email,
  PredefinedQueryOption,
  BroadcastContentType,
  BroadcastMessageContent,
  BroadcastSurveyContent,
  BroadcastReportContent,
  UnpersistedEntity,
  APIAudiencePreviewResponse,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  headers,
  fetchJson,
  renameEntity,
  toRequestOptions,
  Method,
  fetchEntities,
  deleteEntity,
  updateEntity,
  fetchVoid,
} from './api';
import { omit, pick } from 'lodash';

const messageBroadcastContentAttributes: Array<keyof BroadcastMessageContent> =
  ['contentType', 'workspaceId', 'componentType', 'filterConditions'];

const surveyBroadcastContentAttributes: Array<keyof BroadcastSurveyContent> = [
  'contentType',
  'contentId',
  'filterConditions',
];

const reportBroadcastContentAttributes: Array<keyof BroadcastReportContent> = [
  'contentType',
  'contentId',
  'columnName',
  'aggregate',
  'threshold',
  'predicate',
];

const includeOnlyContentTypeSpecificAttributes = (
  broadcast: UnpersistedEntity<APIBroadcastAttributes>
) => {
  switch (broadcast.content.contentType) {
    case BroadcastContentType.MESSAGE:
      return {
        ...broadcast,
        content: pick(broadcast.content, messageBroadcastContentAttributes),
      };
    case BroadcastContentType.SURVEY:
      return {
        ...broadcast,
        content: pick(broadcast.content, surveyBroadcastContentAttributes),
      };
    case BroadcastContentType.REPORT:
      return {
        ...broadcast,
        content: pick(broadcast.content, reportBroadcastContentAttributes),
      };
    default:
      return broadcast;
  }
};

const previewMail = async (
  broadcastId: ArdoqId,
  email: Email
): ApiResponse<BroadcastPreview> => {
  const request = new Request(
    `/api/broadcast/${broadcastId}/preview/mail`,
    toRequestOptions(Method.POST, { email })
  );
  return fetchJson<BroadcastPreview>(request);
};

/**
 * Note that `components` in each audience entry may be an empty array of scope is `ALL`.
 * For these cases the components must be mapped from the `components` field on the broadcast instance.
 */
const getInstances = async (
  broadcastId: ArdoqId
): ApiResponse<BroadcastInstance[]> => {
  const request = new Request(`/api/broadcast/${broadcastId}/instances`, {
    headers,
  });
  return fetchJson<BroadcastInstance[]>(request);
};

const fetchBroadcastInstanceProgress = (
  broadcastInstanceId: ArdoqId
): ApiResponse<APIBroadcastInstanceProgressResponse> => {
  const request = new Request(
    `/api/broadcast/instances/by-id/${broadcastInstanceId}/progress`,
    { headers }
  );
  return fetchJson<APIBroadcastInstanceProgressResponse>(request);
};

type BroadcastsByContentIdResponse = {
  readonly broadcastIds: ArdoqId[];
};

const fetchByContentId = async (contentId: ArdoqId) => {
  const request = new Request(
    `/api/broadcast/content/by-id/${contentId}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<BroadcastsByContentIdResponse>(request);
};

const create = async (
  body: UnpersistedEntity<APIBroadcastAttributes>
): ApiResponse<APIBroadcastAttributes> => {
  const request = new Request(
    '/api/broadcast',
    toRequestOptions(
      Method.POST,
      omit(includeOnlyContentTypeSpecificAttributes(body), [
        '_id',
        '_version',
        'status',
        'nextRuntime',
      ])
    )
  );
  return fetchJson<APIBroadcastAttributes>(request);
};

const setStatus = async (
  broadcastId: ArdoqId,
  status: BroadcastStatus
): ApiResponse<APIBroadcastAttributes> => {
  const request = new Request(
    `/api/broadcast/${broadcastId}/status`,
    toRequestOptions(Method.PUT, { status })
  );
  return fetchJson<APIBroadcastAttributes>(request);
};

const trigger = async (broadcastId: ArdoqId) => {
  const request = new Request(
    `/api/broadcast/${broadcastId}/trigger`,
    toRequestOptions(Method.POST, null)
  );
  return fetchVoid(request);
};
type PredefinedQueryOptionsResponse = {
  options: PredefinedQueryOption[];
};

const getPredefinedQueryOptions = async (
  content: BroadcastContent
): ApiResponse<PredefinedQueryOptionsResponse> => {
  const request = new Request(
    '/api/broadcast/audiences/predefinedoptions',
    toRequestOptions(Method.POST, content)
  );
  return fetchJson<PredefinedQueryOptionsResponse>(request);
};

const previewComponents = async (
  content: BroadcastContent
): ApiResponse<ComponentPreviewResponse> => {
  const request = new Request(
    '/api/broadcast/components',
    toRequestOptions(Method.POST, content)
  );
  return fetchJson<ComponentPreviewResponse>(request);
};

const getAudience = async (
  content: BroadcastContent,
  audiences: BroadcastAudience[]
): ApiResponse<APIAudiencePreviewResponse> => {
  const request = new Request(
    '/api/broadcast/audiences-preview',
    toRequestOptions(Method.POST, { content, audiences })
  );
  return fetchJson<APIAudiencePreviewResponse>(request);
};

const copy = async (
  broadcast: APIBroadcastAttributes,
  newName: string
): ApiResponse<APIBroadcastAttributes> => {
  return broadcastApi.create({ ...broadcast, name: newName });
};

export const broadcastApi = {
  create,
  update: updateEntity<APIBroadcastAttributes>('broadcast'),
  fetchBroadcastInstanceProgress,
  rename: renameEntity('broadcast'),
  fetchByContentId,
  fetchAll: fetchEntities<APIBroadcastAttributes>('broadcast'),
  delete: deleteEntity('broadcast'),
  setStatus,
  trigger,
  previewMail,
  getInstances,
  getPredefinedQueryOptions,
  previewComponents,
  getAudience,
  copy,
};
